import { DatePicker, ErrorText, FlexContent, LoadingSpinner, Tabs } from "@rothoblaas/design-system";
import { FC, lazy, Suspense, useCallback, useMemo, useRef, useState } from "react";
import Header from "../../components/ui/Header";
import { useAuthContext } from "../../context/authContext";
import useI18n from "../../hooks/useTranslations";
import { ACCESS_SCOPE_TITLES, ACCESS_SCOPES } from "../../http/types/accessControl";
import { UsageStatisticsPeriod } from "../../http/types/usageStatistics";
import { useUsageStatisticsApi } from "../../http/useUsageStatistics";
import { formatUtcDay } from "../../utils";
import { colorFromId } from "../../utils/color";
import { formatNumber } from "../../utils/numberFormat";
import {
  addDays,
  DailyStatistic,
  dayToUnixTime,
  DEFAULT_PERIOD_DAYS,
  getDailyUsers,
  getPeriodDays,
  MAX_PERIOD_DAYS,
  todayUtc,
  unixTimeToDay,
} from "./usageStatistics";
import styles from "./AdminUsageStatistics.module.css";

// uPlot in a chunk of its own, loaded with the first chart
const LineChart = lazy(() => import("../../components/ui/LineChart"));

// An app or a module, with its totals over the period.
type UsageItem = { id: string; title: string; visits: number; users: number };

const AdminUsageStatistics: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const today = todayUtc();

  const [period, setPeriod] = useState<UsageStatisticsPeriod>({
    from: addDays(today, 1 - DEFAULT_PERIOD_DAYS),
    to: today,
  });

  const latestTo = addDays(period.from, MAX_PERIOD_DAYS - 1);

  return (
    <>
      <Header
        title={i18n.translation.usageStatistics.plural}
        path={{ display: i18n.translation.usageStatistics.plural }}
      />

      <FlexContent padding noPaddingTop direction="column">
        <div className={styles.filters}>
          <DatePicker
            className={styles.datePicker}
            label={i18n.translation.usageStatistics.from}
            value={period.from}
            minDate={addDays(period.to, 1 - MAX_PERIOD_DAYS)}
            maxDate={period.to}
            locale={auth.identity?.language}
            onChange={(from) => !!from && setPeriod({ ...period, from })}
          />
          <DatePicker
            className={styles.datePicker}
            label={i18n.translation.usageStatistics.to}
            value={period.to}
            minDate={period.from}
            maxDate={latestTo < today ? latestTo : today}
            locale={auth.identity?.language}
            onChange={(to) => !!to && setPeriod({ ...period, to })}
          />
          <span>{i18n.translation.usageStatistics.description}</span>
        </div>

        <Tabs
          renderMode="lazy"
          urlParameter="tab"
          hideBorderBottom
          tabs={[
            { title: i18n.translation.usageStatistics.apps, hasSubTabs: true },
            { title: i18n.translation.modules.plural, hasSubTabs: true },
          ]}
          contents={[<AppStatistics period={period} />, <ModuleStatistics period={period} />]}
        />
      </FlexContent>
    </>
  );
};

const AppStatistics: FC<{ period: UsageStatisticsPeriod }> = ({ period }) => {
  const usageStatisticsApi = useUsageStatisticsApi();
  const aborter = useRef(new AbortController());

  const { data, error, isLoading } = usageStatisticsApi.getAppStatistics(period, aborter.current.signal);

  // every app, the ones without visits too
  const items = useMemo(() => {
    const totals = new Map(data?.totals.map((total) => [total.scope, total]));
    return ACCESS_SCOPES.map((scope) => ({
      id: scope,
      title: ACCESS_SCOPE_TITLES[scope],
      visits: totals.get(scope)?.visits ?? 0,
      users: totals.get(scope)?.users ?? 0,
    }));
  }, [data]);

  const statistics = useMemo(() => data?.days.map(({ date, scope, users }) => ({ date, key: scope, users })), [data]);

  return (
    <UsageStatisticsTabs
      urlParameter="app_tab"
      period={data?.period}
      items={items}
      statistics={statistics}
      error={!!error}
      isRefreshing={isLoading && !!data}
    />
  );
};

const ModuleStatistics: FC<{ period: UsageStatisticsPeriod }> = ({ period }) => {
  const usageStatisticsApi = useUsageStatisticsApi();
  const aborter = useRef(new AbortController());

  const { data, error, isLoading } = usageStatisticsApi.getModuleStatistics(period, aborter.current.signal);

  // only the modules used in the period, by name
  const items = useMemo(
    () =>
      data?.totals.map(({ moduleId, moduleName, visits, users }) => ({
        id: moduleId,
        title: moduleName,
        visits,
        users,
      })) ?? [],
    [data],
  );

  const statistics = useMemo(
    () => data?.days.map(({ date, moduleId, users }) => ({ date, key: moduleId, users })),
    [data],
  );

  return (
    <UsageStatisticsTabs
      urlParameter="module_tab"
      period={data?.period}
      items={items}
      statistics={statistics}
      error={!!error}
      isRefreshing={isLoading && !!data}
    />
  );
};

// An "All" tab with a line per item, then a tab per item.
const UsageStatisticsTabs: FC<{
  urlParameter: string;
  // the period of the statistics
  period?: UsageStatisticsPeriod;
  items: UsageItem[];
  statistics?: DailyStatistic[];
  error: boolean;
  isRefreshing: boolean;
}> = ({ urlParameter, period, items, statistics, error, isRefreshing }) => {
  const i18n = useI18n();

  const days = useMemo(() => (period ? getPeriodDays(period) : []), [period]);
  const x = useMemo(() => days.map(dayToUnixTime), [days]);
  const dailyUsers = useMemo(() => getDailyUsers(days, statistics ?? []), [days, statistics]);
  // the most used first
  const itemsByVisits = useMemo(() => [...items].sort((a, b) => b.visits - a.visits), [items]);
  const singleItems = useMemo(() => items.map((item) => [item]), [items]);

  if (error) {
    return <ErrorText text={i18n.translation.common.errorLoadingData} />;
  }

  if (!period || !statistics) {
    return <LoadingSpinner />;
  }

  if (items.length === 0) {
    return <p>{i18n.translation.common.noItemsFound}</p>;
  }

  return (
    // the nested Tabs is drawn as a sub-tab bar only as Tabs.SubTab's direct child
    <Tabs.SubTab>
      <Tabs
        renderMode="lazy"
        urlParameter={urlParameter}
        tabs={[{ title: i18n.translation.common.all }, ...items.map((item) => ({ title: item.title }))]}
        contents={[
          <UsageStatisticsChart x={x} items={itemsByVisits} dailyUsers={dailyUsers} isRefreshing={isRefreshing} />,
          ...singleItems.map((single) => (
            <UsageStatisticsChart x={x} items={single} dailyUsers={dailyUsers} isRefreshing={isRefreshing} />
          )),
        ]}
      />
    </Tabs.SubTab>
  );
};

const UsageStatisticsChart: FC<{
  x: number[];
  items: UsageItem[];
  dailyUsers: Map<string, number[]>;
  // another period is loading: the chart of the previous one stays, dimmed
  isRefreshing: boolean;
}> = ({ x, items, dailyUsers, isRefreshing }) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const locale = auth.identity?.language;

  const visitsLabel = i18n.translation.usageStatistics.visits;
  const usersLabel = i18n.translation.users.plural;

  // a new series rebuilds the chart: only when the statistics change
  const series = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        label: item.title,
        detail: `${visitsLabel} ${formatNumber(locale, item.visits)} · ${usersLabel} ${formatNumber(locale, item.users)}`,
        color: colorFromId(item.id),
        values: dailyUsers.get(item.id) ?? new Array<number>(x.length).fill(0),
      })),
    [items, dailyUsers, x, locale, visitsLabel, usersLabel],
  );

  const tickFormat = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", timeZone: "UTC" }),
    [locale],
  );
  const formatX = useCallback((value: number) => formatUtcDay(unixTimeToDay(value), locale), [locale]);
  const formatXTick = useCallback((value: number) => tickFormat.format(value * 1000), [tickFormat]);
  const formatValue = useCallback((value: number) => formatNumber(locale, value), [locale]);

  return (
    // the whole width of the tab content, a flex row: else as wide as the legend
    <div style={{ flex: 1, minWidth: 0, opacity: isRefreshing ? 0.5 : 1 }}>
      <Suspense fallback={null}>
        <LineChart
          x={x}
          series={series}
          formatX={formatX}
          formatXTick={formatXTick}
          formatValue={formatValue}
          ariaLabel={i18n.translation.usageStatistics.description}
        />
      </Suspense>
    </div>
  );
};

export default AdminUsageStatistics;
